import React, { useEffect, useState } from "react";
import { X, ExternalLink, CalendarDays } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useConversion } from "@/context/ConversionContext";
import { useCookies } from "@/context/CookieContext";
import { site } from "@/config/site";

export default function CalendlyModal() {
  const { t } = useLang();
  const { calendlyOpen, closeCalendly } = useConversion();
  const { consent } = useCookies();
  const [loadThird, setLoadThird] = useState(false);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && closeCalendly();
    if (calendlyOpen) {
      document.addEventListener("keydown", onKey);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [calendlyOpen, closeCalendly]);

  useEffect(() => {
    if (!calendlyOpen) setLoadThird(false);
  }, [calendlyOpen]);

  if (!calendlyOpen) return null;

  const configured = !!site.calendlyUrl;
  const canEmbed = configured && (consent.marketing || loadThird);

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-6" role="dialog" aria-modal="true" aria-label={t.calendly.title} data-testid="calendly-modal">
      <div className="absolute inset-0 bg-nek-navy/50 backdrop-blur-sm" onClick={closeCalendly} />
      <div className={`relative z-10 flex w-full max-w-3xl flex-col overflow-hidden rounded-t-2xl sm:rounded-2xl bg-white shadow-lift ${configured ? "h-[92vh] sm:h-[85vh]" : "max-h-[92vh]"}`}>
        <div className="flex items-center justify-between border-b border-nek-border px-6 py-4">
          <div>
            <h2 className="text-lg font-heading font-bold text-nek-navy">{t.calendly.title}</h2>
            <p className="text-sm text-nek-gray">{t.calendly.text}</p>
          </div>
          <button type="button" onClick={closeCalendly} aria-label={t.cta.close} data-testid="calendly-close" className="inline-flex h-10 w-10 items-center justify-center rounded-full text-nek-gray hover:bg-nek-light">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-6">
          {!configured ? (
            <div className="flex flex-col items-center justify-center gap-4 py-10 text-center">
              <CalendarDays className="h-12 w-12 text-nek-blue" />
              <p className="max-w-sm text-nek-gray">{t.calendly.notConfigured}</p>
            </div>
          ) : canEmbed ? (
            <iframe
              title={t.calendly.title}
              src={site.calendlyUrl}
              className="h-full min-h-[560px] w-full rounded-xl border border-nek-border"
              data-testid="calendly-iframe"
            />
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-5 text-center">
              <CalendarDays className="h-12 w-12 text-nek-blue" />
              <p className="max-w-md text-nek-gray">{t.calendly.needConsent}</p>
              <div className="flex flex-col gap-3 sm:flex-row">
                <button type="button" onClick={() => setLoadThird(true)} className="btn-navy" data-testid="calendly-allow">
                  {t.calendly.allowAndLoad}
                </button>
                <a href={site.calendlyUrl} target="_blank" rel="noopener noreferrer" className="btn-outline">
                  <ExternalLink className="h-4 w-4" /> {t.calendly.fallback}
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
